import { DateTime } from 'luxon';
import { Order, OrderProduct, OrderReturnStatus, OrderType } from '../../../clients/quipli-service/order/order';
import { ShippingType } from '../../../clients/quipli-service/delivery/delivery';
import { ProductTrackingType } from '../../../clients/quipli-service/product/product';
import { Location } from '../../../clients/quipli-service/location/location';
import { TabOptions } from './dashboard-v2';
import { getOrderStatusLabel } from '../../order/order-utils';
import { DispatchProgress } from '../../../clients/quipli-service/dispatch/dispatch';

/* Date utils */
export const getMinRentalStartDate = (order: Order): DateTime | undefined => {
  if (order.products.length > 0) {
    if (order.products.some((op) => op.type === OrderType.Rental)) {
      return DateTime.min(
        ...order.products
          .filter((op) => op.type === OrderType.Rental)
          .map((op) => op.actualRentalStartDate || op.rentalStartDate),
      );
    } else {
      return DateTime.min(...order.products.map((op) => op.actualRentalStartDate || op.rentalStartDate));
    }
  }
};

export const getMaxRentalEndDate = (order: Order): DateTime | undefined => {
  if (order.products.length > 0) {
    if (order.products.some((op) => op.type === OrderType.Rental)) {
      return DateTime.max(
        ...order.products
          .filter((op) => op.type === OrderType.Rental)
          .flatMap((op) => (op.rentalEndDate ? [op.actualRentalEndDate || op.rentalEndDate] : [])),
      );
    } else {
      return DateTime.max(
        ...order.products.flatMap((op) => (op.rentalEndDate ? [op.actualRentalEndDate || op.rentalEndDate] : [])),
      );
    }
  }
};

/* Sorting utils */
export const isActionable = (order: OrderCardProcessedDetails) => {
  const nextAction: OrderReturnStatus = order.order.returnStatus;

  // Make any overdue actionable
  if (order.overDueAmount) {
    return true;
  }

  let actionable = false;
  if (order.direction.includes(OrderDirection.GoingOut)) {
    actionable = nextAction === OrderReturnStatus.NotStarted;
  }

  if (order.direction.includes(OrderDirection.ComingIn)) {
    actionable = actionable
      ? actionable
      : nextAction === OrderReturnStatus.OnRent || nextAction === OrderReturnStatus.OffRent;
  }

  return actionable;
};

export const sortOrders = (orders: OrderCardProcessedDetails[], selectedTab: TabOptions) =>
  orders.sort((a: OrderCardProcessedDetails, b: OrderCardProcessedDetails) => {
    const aActionable = isActionable(a);
    const bActionable = isActionable(b);

    if (aActionable && !bActionable) {
      return Number.MIN_SAFE_INTEGER;
    }
    if (!aActionable && bActionable) {
      return Number.MAX_SAFE_INTEGER;
    }

    // Coming in orders we want first "ending"
    if (selectedTab === TabOptions.ComingIn) {
      return (a.rentalEndDate as DateTime).toMillis() - (b.rentalEndDate as DateTime).toMillis();
    }

    // Going out orders, or "All", we want first "starting"
    return (a.rentalStartDate as DateTime).toMillis() - (b.rentalStartDate as DateTime).toMillis();
  });

// TODO: need to evaluate how we'll do this for an order level, right now just taking if any have delivery
export const hasAnyDelivery = (order: Order): boolean => {
  return order.products.some((product) => product.shippingType !== ShippingType.SelfPickUp);
};

/* Conversions for something more usable/testable by the component */
export enum OrderDirection {
  ComingIn = 'Coming in',
  GoingOut = 'Going out',
}

export type OrderCardProduct = {
  name: string;
  quantity: number;
  unit?: string;
  serial?: string;
  trackingType?: ProductTrackingType;
  id?: number;
};

// Type wrapper to make it easier to test the component
export type OrderCardProcessedDetails = {
  direction: ('Coming in' | 'Going out')[];
  customerName: string;
  customerPhone: string;
  orderNumber: string;
  location: string;
  fullLocation: Location | undefined;
  orderStatus: string;
  orderProducts: OrderCardProduct[];
  rentalStartDate: DateTime | undefined;
  rentalEndDate: DateTime | undefined;
  scheduledDate?: DateTime;
  deliveryDriverId?: number;
  pickupDriverId?: number;
  orderTotal: number;
  paymentMethod: string;
  orderBalance: number;
  isPaid: boolean;
  isOnAccount: boolean;
  overDueAmount?: string;
  overDueGreater2Hours?: boolean;
  isDelivery?: boolean;
  deliveryTime?: string;
  shippingAddress?: string;
  order: Order;
  dispatchStatus?: DispatchProgress;
};

const pickFirstProductProperty = (order: Order, property: keyof OrderProduct) =>
  order.products.find((product) => product[property] !== null)?.[property];

export const orderToOrderCardPropsMapper = (
  order: Order,
  timeNow: DateTime,
  location: Location | undefined,
  direction: OrderDirection[],
): OrderCardProcessedDetails => {
  const overDueCalc = overDueAmount(order, timeNow);

  const orderCardProps = {
    direction: direction,
    customerName: order.user?.name || '',
    customerPhone: order.user?.phone || '',
    orderNumber: order.orderNumber,

    // TODO: this has to be from the dropdown for now, so not allowing multiple
    location: location?.name ?? 'Unknown',
    fullLocation: location,
    orderStatus: getOrderStatusLabel(order),
    orderProducts: order.products.map((product) => ({
      name: product.name,
      quantity: product.quantity,
      serial:
        product.assignedUnits.length > 0
          ? product.assignedUnits.map((unit) => unit.serialNumber).join(', ')
          : undefined,
      trackingType: product.trackingType,
      id: product.product.id,
      unit:
        product.assignedUnits.length > 0 ? product.assignedUnits.map((unit) => unit.unitNumber).join(', ') : undefined,
    })),
    rentalStartDate: getMinRentalStartDate(order),
    rentalEndDate: getMaxRentalEndDate(order),
    orderTotal: order.invoices.reduce((acc, invoice) => acc + invoice.total, 0),
    paymentMethod: order.paymentMethod.type,

    orderBalance: order.invoices.reduce((acc, invoice) => acc + invoice.balance, 0),
    isPaid: order.invoices.filter((invoice) => invoice.balance !== 0).length === 0,
    isOnAccount: order.paymentMethod.type === 'On Account',

    deliveryDriverId: Number(pickFirstProductProperty(order, 'deliveryDriverId')),
    pickupDriverId: Number(pickFirstProductProperty(order, 'pickupDriverId')),

    overDueAmount: overDueCalc.diff,
    overDueGreater2Hours: overDueCalc.greaterThan2Hours,
    isDelivery: hasAnyDelivery(order),
    // TODO: delivery time needs to be re-evaluated
    deliveryTime: 'right now',
    shippingAddress: order.shippingAddress?.address1,
    order: order,
  };

  return orderCardProps;
};

type OverDueAmount = {
  diff: string;
  greaterThan2Hours: boolean;
};

export const overDueAmount = (order: Order, timeNow: DateTime): OverDueAmount => {
  const orderReturnStatus = order.returnStatus;

  const orderStartDate = getMinRentalStartDate(order);

  const orderEndDate = getMaxRentalEndDate(order);

  const diffFormat = "d'd 'h'h 'm'm'";

  let diff = undefined;
  // This will need to consider start date
  if ([OrderReturnStatus.NotStarted].includes(orderReturnStatus)) {
    if (orderStartDate && timeNow > orderStartDate) {
      diff = timeNow.diff(orderStartDate);
    }
  }

  // This will need to consider end date
  if ([OrderReturnStatus.OnRent, OrderReturnStatus.OffRent].includes(orderReturnStatus)) {
    if (orderEndDate && timeNow > orderEndDate) {
      diff = timeNow.diff(orderEndDate);
    }
  }

  const greaterThan2Hours = diff !== undefined && diff.as('hours') > 2;
  return {
    diff: diff ? diff.toFormat(diffFormat) + ' overdue' : '',
    greaterThan2Hours,
  };
};
